import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KEPT_CHANGED_EVENT, capOf, keptCount, uploadKept } from '../services/vaultCloudCap';
import { setPlanModalOpen } from '../store/uiModalStore';
import './VaultCapMeter.css';

/**
 * "N items are kept on this device only" under Settings → Vault → Cloud copy
 * (card U1) — the way back from the cap card's "Keep on this device only".
 * With room in the cloud (an upgrade, or items removed) they upload with one
 * click; still full, the way on is the upgrade. Nothing shows when none are kept.
 */
export default function VaultCapKept({ consent }) {
  const { t } = useTranslation();
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const cap = capOf(consent);

  const read = useCallback(async () => setCount(await keptCount()), []);
  useEffect(() => {
    read();
    window.addEventListener(KEPT_CHANGED_EVENT, read);
    return () => window.removeEventListener(KEPT_CHANGED_EVENT, read);
  }, [read, consent]);

  if (!cap.cloud || count === 0) return null;
  const room = cap.max == null || cap.items + count <= cap.max;

  const upload = async () => {
    setBusy(true);
    setError('');
    try {
      await uploadKept();
      await read();
    } catch {
      setError(t('vault.capKeptUploadFailed', 'That did not work. Try again.'));
    }
    setBusy(false);
  };

  return (
    <div className="vault-cap-meter" data-testid="vault-cap-kept">
      <div className="vault-cap-meter-label">
        {t('vault.capKept', '{{count}} items are kept on this device only', { count })}
      </div>
      <div className="vault-cap-meter-hint">
        {room
          ? t('vault.capKeptRoom', 'Your cloud vault has room for them now.')
          : t('vault.capKeptFull', 'Your free cloud vault has no room for them.')}{' '}
        {room ? (
          <button type="button" className="vault-cap-meter-link" disabled={busy} onClick={upload}>
            {t('vault.capKeptUpload', 'Upload them to your cloud vault')}
          </button>
        ) : (
          <button type="button" className="vault-cap-meter-link" onClick={() => setPlanModalOpen(true)}>
            {t('vault.capUpgradeShort', 'Upgrade')}
          </button>
        )}
      </div>
      {error && <div className="vault-modal-error">{error}</div>}
    </div>
  );
}
